import { BufferAttribute, BufferGeometry, Color, Float32BufferAttribute, IcosahedronGeometry, Mesh, ShaderMaterial } from 'three';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { noise3 } from './random';
import { COMMON_GLSL } from './seabedShaders';

// Rocas: esferas deformadas con ruido (aspecto de piedra erosionada), sombreado suave, grano en el shader,
// parcialmente enterradas en la arena. Todas se fusionan en UNA geometría (un draw call).
// Luz: cara superior iluminada desde la superficie + caustics, luz de borde, oclusión y polvo de arena en la base.

const VERT = /* glsl */`
  attribute float aH;       // altura sobre el suelo / tamaño de la roca (0 = en contacto con la arena)
  varying vec3 vWorld;
  varying vec3 vN;
  varying vec3 vObj;
  varying float vH;
  void main() {
    vN = normalize(mat3(modelMatrix) * normal);
    vObj = position;
    vH = aH;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    gl_Position = projectionMatrix * viewMatrix * w;
  }
`;

const FRAG = /* glsl */`
  precision highp float;
  uniform float uCausticTime, uCausticScale, uCausticStrength;
  uniform vec3 uRockDark, uRockLight, uRim, uSurfaceLight, uCausticColor, uSandDark;
  varying vec3 vWorld;
  varying vec3 vN;
  varying vec3 vObj;
  varying float vH;
  ${COMMON_GLSL}

  float grain(vec3 p) {
    #ifdef ROCK_BUMP
    return 0.55 * vnoise(p * 0.012) + 0.3 * vnoise(p * 0.035) + 0.15 * vnoise(p * 0.11);
    #else
    return 0.65 * vnoise(p * 0.012) + 0.35 * vnoise(p * 0.04);   // móvil: sin relieve, solo variación de tono
    #endif
  }

  void main() {
    vec3 n = normalize(vN);
    float dist = distance(cameraPosition, vWorld);

    // Relieve de la piedra: gradiente del grano (diferencias finitas) perturba la normal.
    float g0 = grain(vObj);
    #ifdef ROCK_BUMP
    vec3 e = vec3(6.0, 0.0, 0.0);
    vec3 grad = vec3(grain(vObj + e.xyy) - g0, grain(vObj + e.yxy) - g0, grain(vObj + e.yyx) - g0);
    n = normalize(n - grad * 5.0);
    #endif

    float hemi = 0.5 + 0.5 * n.y;
    float dif = max(dot(n, normalize(vec3(-0.3, 1.0, -0.25))), 0.0);
    float pool = lightPool(vWorld);                                  // mismos haces de luz que el suelo
    vec3 col = mix(uRockDark, uRockLight, 0.2 + 0.8 * g0) * (0.1 + 0.9 * hemi * hemi) * (0.45 + 0.75 * pool);
    col += uSurfaceLight * pow(dif, 1.5) * 0.9 * pool;               // luz de la superficie: fuerte y dirigida

    float up = smoothstep(0.1, 0.9, n.y);
    float c = caustic(vWorld.xz / uCausticScale + vWorld.y / uCausticScale * 0.6, uCausticTime);
    col += uCausticColor * c * up * uCausticStrength * pool * (1.0 - smoothstep(1800.0, 6000.0, dist));
    col += uRim * pow(1.0 - abs(dot(n, normalize(cameraPosition - vWorld))), 3.0) * 0.45 * (0.3 + 0.7 * hemi);

    // Base enterrada: se oscurece y se tiñe de arena.
    float contact = 1.0 - smoothstep(0.0, 0.3, vH);
    col = mix(col, uSandDark * 1.6, contact * 0.8);

    col = applyFog(col, dist);
    gl_FragColor = vec4(col, 1.0);
  }
`;

export default class SeabedRocks {
  // opts: { rocks: [{x, y, z, size, squash, rot}], heightAt, uniforms, defines, settings, seed }
  constructor(opts) {
    const { rocks, heightAt, seed } = opts;
    const base = new IcosahedronGeometry(1, opts.mobile ? 3 : 4);
    base.deleteAttribute('uv');
    base.deleteAttribute('normal');
    const unit = mergeVertices(base);                      // indexada → normales suaves
    base.dispose();
    const up = unit.attributes.position, per = up.count;
    const uIdx = unit.index.array;

    const pos = new Float32Array(per * rocks.length * 3);
    const aH = new Float32Array(per * rocks.length);
    const idx = new Uint32Array(uIdx.length * rocks.length);
    let o = 0, h = 0, ii = 0;
    rocks.forEach((rock, ri) => {
      const sd = seed + ri * 13.7;
      const cr = Math.cos(rock.rot), sr = Math.sin(rock.rot);
      const rx = rock.size, ry = rock.size * rock.squash, rz = rock.size * 0.85;
      for (let i = 0; i < per; i++) {
        let x = up.getX(i), y = up.getY(i), z = up.getZ(i);
        // Piedra erosionada: ruido a 2 escalas + planos suaves en la parte baja.
        const k = 1 + 0.3 * noise3(x * 1.3 + sd, y * 1.3, z * 1.3) + 0.12 * noise3(x * 3.1 + sd, y * 3.1, z * 3.1);
        x *= k * rx; y = Math.max(y, -0.55) * k * ry; z *= k * rz;
        const wx = x * cr + z * sr + rock.x, wz = -x * sr + z * cr + rock.z, wy = y + rock.y;
        pos[o++] = wx; pos[o++] = wy; pos[o++] = wz;
        aH[h++] = Math.max(0, (wy - heightAt(wx, wz)) / rock.size);
      }
      for (let i = 0; i < uIdx.length; i++) idx[ii++] = uIdx[i] + ri * per;
    });
    unit.dispose();

    const geo = new BufferGeometry();
    geo.setAttribute('position', new Float32BufferAttribute(pos, 3));
    geo.setAttribute('aH', new Float32BufferAttribute(aH, 1));
    geo.setIndex(new BufferAttribute(idx, 1));
    geo.computeVertexNormals();

    const s = opts.settings;
    const mat = new ShaderMaterial({
      defines: opts.defines,
      uniforms: {
        ...opts.uniforms,
        uRockDark: { value: new Color(s.rockDark) },
        uRockLight: { value: new Color(s.rockLight) },
        uRim: { value: new Color(s.rim) },
        uSurfaceLight: { value: new Color(s.surfaceLight) },
        uSandDark: { value: new Color(s.sandDark) },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
    });
    this.mesh = new Mesh(geo, mat);
    this.mesh.frustumCulled = false;
  }

  dispose() {
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
